/**
 * useOnboarding — the React binding for the Team Onboarding feature.
 *
 * Strict layering: the OnboardingPage calls THIS hook; the hook reaches the
 * backend only through the api facade (`api.teamOnboarding`) and never touches
 * fetch/ApiClient. It owns the wizard's client state: the team fields, the
 * editable account drafts (with per-row validation), the start result, and the
 * fetched onboarding status.
 */

import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useApi } from '../../hooks/useApi';
import { ApiError } from '../../api';
import {
  TeamOnboardingApi,
  type OnboardingAccount,
  type OwnedBindle,
  type StartTeamOnboardingResponse,
} from '../../api';
import {
  DEFAULT_REGION,
  draftToEntry,
  isDraftComplete,
  roleArnForAccount,
  type AccountDraft,
  type OnboardingStage,
} from './types';

/** Creates a fresh, empty account draft with the region prefilled. */
function newDraft(): AccountDraft {
  return {
    id: uuidv4(),
    accountId: '',
    stage: 'beta',
    region: DEFAULT_REGION,
    roleArn: '',
    roleArnAuto: true,
    validation: 'idle',
  };
}

export interface OnboardingStatusView {
  status: string;
  accounts: OnboardingAccount[];
}

export interface UseOnboardingResult {
  // Team fields
  teamName: string;
  setTeamName: (v: string) => void;
  bindleId: string;
  setBindleId: (v: string) => void;

  // Bindle picker (list the caller-supplied team's owned bindles)
  teamId: string;
  setTeamId: (v: string) => void;
  listBindles: () => Promise<void>;
  bindlesLoading: boolean;
  bindles: OwnedBindle[] | null;
  bindlesError: string | null;
  /** True when a team id is entered (List my bindles is enabled). */
  canListBindles: boolean;
  /** Fills the Bindle id field from a picked owned bindle. */
  selectBindle: (bindleId: string) => void;

  // Account drafts
  accounts: AccountDraft[];
  addAccount: () => void;
  removeAccount: (id: string) => void;
  updateAccount: (id: string, patch: Partial<AccountDraft>) => void;
  validateAccount: (id: string) => Promise<void>;

  // Start
  start: () => Promise<void>;
  starting: boolean;
  startResult: StartTeamOnboardingResponse | null;
  startError: string | null;
  /** True when at least one complete account exists (Start is enabled). */
  canStart: boolean;

  // Status
  refreshStatus: () => Promise<void>;
  statusLoading: boolean;
  status: OnboardingStatusView | null;
  statusError: string | null;
}

function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

export function useOnboarding(): UseOnboardingResult {
  const api = useApi();

  const [teamName, setTeamName] = useState('');
  const [bindleId, setBindleId] = useState('');
  const [accounts, setAccounts] = useState<AccountDraft[]>([newDraft()]);

  const [teamId, setTeamId] = useState('');
  const [bindles, setBindles] = useState<OwnedBindle[] | null>(null);
  const [bindlesLoading, setBindlesLoading] = useState(false);
  const [bindlesError, setBindlesError] = useState<string | null>(null);

  const [starting, setStarting] = useState(false);
  const [startResult, setStartResult] =
    useState<StartTeamOnboardingResponse | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  const [statusLoading, setStatusLoading] = useState(false);
  const [status, setStatus] = useState<OnboardingStatusView | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Keep the latest accounts for async closures (validate/start) without
  // re-creating callbacks on every keystroke.
  const accountsRef = useRef<AccountDraft[]>(accounts);
  accountsRef.current = accounts;

  const addAccount = useCallback(() => {
    setAccounts((prev) => [...prev, newDraft()]);
  }, []);

  const removeAccount = useCallback((id: string) => {
    setAccounts((prev) =>
      prev.length <= 1 ? prev : prev.filter((a) => a.id !== id),
    );
  }, []);

  const updateAccount = useCallback(
    (id: string, patch: Partial<AccountDraft>) => {
      setAccounts((prev) =>
        prev.map((a) => {
          if (a.id !== id) return a;
          const next: AccountDraft = { ...a, ...patch };
          // Auto-derive roleArn from accountId while the user hasn't hand-edited
          // it, so the required convention is always prefilled.
          if (patch.accountId !== undefined && next.roleArnAuto) {
            next.roleArn = roleArnForAccount(next.accountId);
          }
          // Any edit to identifying fields invalidates a prior validation.
          if (
            patch.accountId !== undefined ||
            patch.roleArn !== undefined
          ) {
            next.validation = 'idle';
            delete next.errorMessage;
          }
          return next;
        }),
      );
    },
    [],
  );

  const validateAccount = useCallback(
    async (id: string) => {
      const draft = accountsRef.current.find((a) => a.id === id);
      if (!draft || !isDraftComplete(draft)) return;

      setAccounts((prev) =>
        prev.map((a) =>
          a.id === id
            ? { ...a, validation: 'validating', errorMessage: undefined }
            : a,
        ),
      );

      try {
        const res = await api.teamOnboarding.validateAccount({
          accountId: draft.accountId.trim(),
          roleArn: draft.roleArn.trim(),
          ...(bindleId.trim() ? { bindleId: bindleId.trim() } : {}),
        });
        setAccounts((prev) =>
          prev.map((a) =>
            a.id === id
              ? {
                  ...a,
                  validation: res.valid ? 'valid' : 'invalid',
                  ...(res.valid
                    ? { errorMessage: undefined }
                    : {
                        errorMessage:
                          res.errorMessage ?? 'Role validation failed.',
                      }),
                }
              : a,
          ),
        );
      } catch (e) {
        setAccounts((prev) =>
          prev.map((a) =>
            a.id === id
              ? {
                  ...a,
                  validation: 'invalid',
                  errorMessage: errMessage(e, 'Validation request failed.'),
                }
              : a,
          ),
        );
      }
    },
    [api, bindleId],
  );

  const completeDrafts = accounts.filter(isDraftComplete);
  const canStart = completeDrafts.length > 0;

  const start = useCallback(async () => {
    const ready = accountsRef.current.filter(isDraftComplete);
    if (ready.length === 0) {
      setStartError('Add at least one account with all fields filled.');
      return;
    }
    if (!teamName.trim() || !bindleId.trim()) {
      setStartError('Team name and Bindle id are required.');
      return;
    }
    setStarting(true);
    setStartError(null);
    setStartResult(null);
    try {
      const res = await api.teamOnboarding.startTeamOnboarding({
        teamName: teamName.trim(),
        bindleId: bindleId.trim(),
        accounts: ready.map(draftToEntry),
      });
      setStartResult(res);
    } catch (e) {
      setStartError(errMessage(e, 'Failed to start onboarding.'));
    } finally {
      setStarting(false);
    }
  }, [api, teamName, bindleId]);

  const refreshStatus = useCallback(async () => {
    if (!bindleId.trim()) {
      setStatusError('Enter a Bindle id to check status.');
      return;
    }
    setStatusLoading(true);
    setStatusError(null);
    try {
      const res = await api.teamOnboarding.getStatus({
        bindleId: bindleId.trim(),
      });
      setStatus({
        status: res.status,
        accounts: TeamOnboardingApi.parseAccounts(res.accounts),
      });
    } catch (e) {
      setStatus(null);
      setStatusError(errMessage(e, 'Failed to load onboarding status.'));
    } finally {
      setStatusLoading(false);
    }
  }, [api, bindleId]);

  const canListBindles = teamId.trim().length > 0;

  const listBindles = useCallback(async () => {
    if (!teamId.trim()) {
      setBindlesError('Enter a Team id to list owned bindles.');
      return;
    }
    setBindlesLoading(true);
    setBindlesError(null);
    try {
      const list = await api.teamOnboarding.listOwnerBindles({
        teamId: teamId.trim(),
      });
      setBindles(list);
    } catch (e) {
      setBindles(null);
      setBindlesError(errMessage(e, 'Failed to list owned bindles.'));
    } finally {
      setBindlesLoading(false);
    }
  }, [api, teamId]);

  const selectBindle = useCallback((picked: string) => {
    setBindleId(picked);
  }, []);

  return {
    teamName,
    setTeamName,
    bindleId,
    setBindleId,
    teamId,
    setTeamId,
    listBindles,
    bindlesLoading,
    bindles,
    bindlesError,
    canListBindles,
    selectBindle,
    accounts,
    addAccount,
    removeAccount,
    updateAccount,
    validateAccount,
    start,
    starting,
    startResult,
    startError,
    canStart,
    refreshStatus,
    statusLoading,
    status,
    statusError,
  };
}

// Re-export the stage type for the page's <Select> options.
export type { OnboardingStage };
